import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* Scroll Animation Keyframes */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeInLeft {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes fadeInRight {
    from { opacity: 0; transform: translateX(30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes fadeInOpacity {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Animation Classes */
  .animate-on-scroll {
    opacity: 0;
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }

  .animate-on-scroll.visible {
    opacity: 1;
  }

  .fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .fade-in-down {
    animation: fadeInDown 0.6s ease-out forwards;
  }

  .fade-in-left {
    animation: fadeInLeft 0.6s ease-out forwards;
  }

  .fade-in-right {
    animation: fadeInRight 0.6s ease-out forwards;
  }

  .scale-in {
    animation: scaleIn 0.6s ease-out forwards;
  }

  .slide-up {
    animation: slideUp 0.6s ease-out forwards;
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    overflow-x: hidden;
    /* Avoid transforms on html/body that can break position: fixed stacking/containment */
    animation: fadeInOpacity 220ms ease-out both;
  }

  body {
    font-family: ${props => props.theme.fonts.secondary};
    font-size: ${props => props.theme.fontSizes.base};
    font-weight: ${props => props.theme.fontWeights.normal};
    line-height: 1.6;
    color: ${props => props.theme.colors.dark};
    background-color: ${props => props.theme.colors.light};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    width: 100%;
  }

  /* Reserve space for the fixed header globally so first section isn't hidden */
  main {
    padding-top: var(--header-offset, 0px);
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.primary};
    font-weight: ${props => props.theme.fontWeights.bold};
    line-height: 1.2;
    margin-bottom: ${props => props.theme.spacing.md};
  }

  h1 {
    font-size: ${props => props.theme.fontSizes['5xl']};
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['4xl']};
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['3xl']};
    }
  }

  h2 {
    font-size: ${props => props.theme.fontSizes['4xl']};
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['3xl']};
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['2xl']};
    }
  }

  h3 {
    font-size: ${props => props.theme.fontSizes['2xl']};
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.xl};
    }
  }

  h4 {
    font-size: ${props => props.theme.fontSizes.xl};
  }

  h5 {
    font-size: ${props => props.theme.fontSizes.lg};
  }

  h6 {
    font-size: ${props => props.theme.fontSizes.base};
  }

  p {
    margin-bottom: ${props => props.theme.spacing.md};
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: ${props => props.theme.transitions.default};

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }

  ul, ol {
    margin-bottom: ${props => props.theme.spacing.md};
    padding-left: ${props => props.theme.spacing.xl};
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Utility: make an img fill its box without cropping */
  .img-contain, .fit-contain {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    background: ${props => props.theme.colors.gray[50]};
    display: block;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    font-family: inherit;
    transition: ${props => props.theme.transitions.default};
    will-change: transform, box-shadow;
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    outline: none;
    transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
  }

  .container {
    max-width: ${props => props.theme.breakpoints.wide};
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing.lg};
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      padding: 0 ${props => props.theme.spacing.md};
    }
  }

  .section-padding {
    padding: ${props => props.theme.spacing['4xl']} 0;
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      padding: ${props => props.theme.spacing['3xl']} 0;
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      padding: ${props => props.theme.spacing['2xl']} 0;
    }
  }

  .text-center {
    text-align: center;
  }

  .text-gradient {
    background: ${props => props.theme.colors.gradient.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* Smooth interactive helpers */
  .elevate-on-hover {
    transition: ${props => props.theme.transitions.default};
  }
  .elevate-on-hover:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  /* Consistent focus ring */
  :focus-visible {
    outline: 3px solid rgba(0,168,232,0.35);
    outline-offset: 2px;
  }

  /* Scroll anchor offset for headings */
  h2, h3, h4 { scroll-margin-top: 96px; }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    *, *::before, *::after {
      animation: none !important;
      transition: none !important;
    }
  }

  // Scrollbar styles
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.gray[200]};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: ${props => props.theme.borderRadius.full};

    &:hover {
      background: ${props => props.theme.colors.secondary};
    }
  }
`;